import { useState } from "react";

import { OpenInNew as OpenInNewIcon } from "@mui/icons-material";
import { CircularProgress, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { GridColDef } from "@mui/x-data-grid";

import { signedUrlActions } from "../../../../../API";
import DownloadIcon from "../../../../../common/components/icons/DownloadIcon";
import { MediaFullScreen } from "../../../../../common/components/media/MediaFullScreen";
import TableCellTruncated from "../../../../../common/components/table/TableCellTruncated";
import useLocationFromCache from "../../../../../common/hooks/useLocationFromCache";
import { useS3ObjectExists } from "../../../../../common/hooks/useS3ObjectExists";
import { VideoStatusEnum } from "../../../../../common/models/enums";
import {
  S3BucketKeys,
  S3BucketKeyType,
} from "../../../../../common/utils/s3helper";
import { formatTimestamp } from "../../../../../common/utils/timestampUtils";
import {
  errorNotification,
  successNotification,
} from "../../../../../common/variables/notification";
import { useGetSignedUrlAction } from "../../../../../leak-finder/hooks/useGetSignedUrlAction";
import { ISecurityEventsTableRow } from "../../../../data-hub/components/models/common/events.models";
import { HardHatColumnFields } from "../../../../data-hub/components/models/hardhat/enums/HardHatColumnFields";
import CountDelta from "../../../components/CountDelta";
import LiveViewButtonContainer from "../../../components/LiveViewButtonContainer";
import RetrieveVideoButton from "../../../components/RetrieveVideoButton";
import VideoDialog from "../../../../../common/components/media/VideoDialog";
import { DEFAULT_MEDIA_ICON_STYLES } from "../../../../../common/variables/common";

export const getRecordingS3Key = (s3Key: string) => {
  const s3KeyModified = (() => {
    // Ensure '/recording/' is inserted after the first 4 path segments
    // (i.e., as the 5th segment), replacing an existing 'fetched'/'recording' if present
    const withRecording = s3Key.replace(
      /^((?:[^/]*\/){4})(?:(?:fetched|recording)\/)?/,
      "$1recording/"
    );

    // Use the 4th segment (index 3) for the filename prefix
    const segmentsForName = withRecording.split("/").filter(Boolean);
    const fourthSegmentForName = segmentsForName[3];

    return withRecording.replace(/\/([^/]+)$/, (_m, filename) => {
      const lastDotIndex = filename.lastIndexOf(".");
      const baseName =
        lastDotIndex >= 0 ? filename.slice(0, lastDotIndex) : filename;
      const firstUnderscoreIndex = baseName.indexOf("_");
      const suffix =
        firstUnderscoreIndex >= 0
          ? baseName.slice(firstUnderscoreIndex + 1)
          : baseName;

      const modifiedS3Key = `/${fourthSegmentForName}_${suffix}.mp4`;

      return modifiedS3Key;
    });
  })();

  return s3KeyModified;
};

interface S3MediaActionsProps {
  s3Key?: string | null;
  loading?: boolean;
  exists?: boolean;
}

const S3MediaActions = ({ s3Key, loading, exists }: S3MediaActionsProps) => {
  const { fetchSignedUrlAction } = useGetSignedUrlAction();

  const [downloadLoading, setDownloadLoading] = useState(false);

  const [open, setOpen] = useState(false);

  const [s3FetchedVideoSignedUrl, setS3FetchedVideoSignedUrl] = useState("");

  const handleView = async () => {
    if (!s3Key) {
      errorNotification("Error viewing video");

      return;
    }

    const s3KeyModified = getRecordingS3Key(s3Key);

    if (!s3KeyModified) {
      errorNotification("Error viewing video");

      return;
    }

    try {
      const signedUrl = await fetchSignedUrlAction({
        objectKey: s3KeyModified.replace(/^\/+/, ""),
        operation: signedUrlActions.getObject,
        bucketKey: S3BucketKeys.NODE_FILES,
      });

      setS3FetchedVideoSignedUrl(signedUrl);

      setOpen(true);
    } catch (error) {
      console.error(error);

      errorNotification("Error viewing video");
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleVideoFullScreenClose = () => {
    setOpen(false);

    setS3FetchedVideoSignedUrl("");
  };

  const handleDownload = async () => {
    if (downloadLoading) return;

    try {
      if (!s3Key) return;

      setDownloadLoading(true);

      const s3KeyModified = getRecordingS3Key(s3Key);

      if (!s3KeyModified) {
        setDownloadLoading(false);

        return;
      }

      const signedUrl = await fetchSignedUrlAction({
        objectKey: s3KeyModified.replace(/^\/+/, ""),
        operation: signedUrlActions.getObject,
        bucketKey: S3BucketKeys.NODE_FILES,
      });

      if (signedUrl) {
        const response = await fetch(signedUrl);

        const blob = await response.blob();

        const urlObject = URL.createObjectURL(blob);

        const link = document.createElement("a");

        link.href = urlObject;

        const name = s3KeyModified.split("/").pop() || "media.mp4";

        link.setAttribute("download", name);

        document.body.appendChild(link);

        link.click();

        URL.revokeObjectURL(urlObject);

        link.remove();
      }
    } catch (error) {
      console.error(error);

      errorNotification("Error downloading video");
    } finally {
      setDownloadLoading(false);

      successNotification("Video is downloaded");
    }
  };

  return (
    <>
      <Button
        onClick={handleDownload}
        variant="contained"
        size="small"
        disableRipple
        startIcon={
          downloadLoading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            <DownloadIcon />
          )
        }
        disabled={loading || !exists || downloadLoading}
      >
        <Typography variant="buttonMedium">Download</Typography>
      </Button>
      <Button
        onClick={handleView}
        variant="contained"
        size="small"
        disableRipple
        startIcon={<OpenInNewIcon />}
        disabled={loading || !exists || downloadLoading}
      >
        <Typography variant="buttonMedium">View</Typography>
      </Button>

      {open && (
        <VideoDialog
          videoUrl={s3FetchedVideoSignedUrl}
          expanded={!!s3FetchedVideoSignedUrl}
          toggleExpand={handleVideoFullScreenClose}
        />
      )}
    </>
  );
};

export const useGetSecurityEventsTableColumns = () => {
  const { getCachedLocationName } = useLocationFromCache();

  const columns: GridColDef<ISecurityEventsTableRow>[] = [
    {
      field: "mediaOutput",
      headerName: "Preview",
      flex: 1,
      minWidth: 160,
      width: 160,
      hideable: false,
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params): JSX.Element | null => {
        return (
          <MediaFullScreen
            keyFrame={params.row.signedUrlMediaOutput}
            s3ImageFullScreenProps={{
              sx: DEFAULT_MEDIA_ICON_STYLES,
              fixedHeight: "75px",
            }}
          />
        );
      },
    },
    {
      field: "location",
      headerName: "Location",
      flex: 1,
      minWidth: 160,
      hideable: false,
      disableColumnMenu: true,
      sortable: false,
      valueGetter: (locationId?: string | null): string => {
        const locationName = getCachedLocationName(locationId);

        return locationName;
      },
      renderCell: (params): JSX.Element | null => {
        const locationName = getCachedLocationName(params?.row?.location ?? "");

        return (
          <TableCellTruncated
            tooltipText={locationName}
            text={locationName}
            TypographyProps={{
              variant: "subtitle1",
            }}
          />
        );
      },
    },
    {
      field: "peopleCount",
      headerName: "People count",
      flex: 1,
      minWidth: 160,
      hideable: false,
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params): JSX.Element | null => {
        if (!params) {
          return null;
        }

        const peopleCount = params?.row?.peopleCount;
        const peopleDelta = params?.row?.peopleDelta;

        return (
          <Box display="flex" alignItems="center" gap={1}>
            <TableCellTruncated
              tooltipText={""}
              text={peopleCount}
              TypographyProps={{
                variant: "body2",
              }}
            />
            <CountDelta delta={peopleDelta} />
          </Box>
        );
      },
    },
    {
      field: "vehicleCount",
      headerName: "Vehicle count",
      flex: 1,
      minWidth: 160,
      hideable: false,
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params): JSX.Element | null => {
        if (!params) {
          return null;
        }

        const vehicleCount = params?.row?.vehicleCount;
        const vehicleDelta = params?.row?.vehicleDelta;

        return (
          <Box display="flex" alignItems="center" gap={1}>
            <TableCellTruncated
              tooltipText={""}
              text={vehicleCount}
              TypographyProps={{
                variant: "body2",
              }}
            />
            <CountDelta delta={vehicleDelta} />
          </Box>
        );
      },
    },
    {
      field: "cameraId",
      headerName: "DEVICE ID",
      flex: 1,
      minWidth: 160,
      hideable: false,
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params): JSX.Element | null =>
        params ? (
          <TableCellTruncated
            tooltipText={params?.row.cameraId}
            text={params?.row.cameraId}
            TypographyProps={{
              variant: "body2Bold",
            }}
          />
        ) : null,
    },
    {
      field: HardHatColumnFields.Timestamp,
      headerName: "Timestamp",
      flex: 1,
      minWidth: 180,
      hideable: false,
      disableColumnMenu: true,
      sortable: false,
      valueFormatter: (timestamp: string): string => formatTimestamp(timestamp),
      renderCell: (params): JSX.Element | null =>
        params ? (
          <TableCellTruncated
            tooltipText={formatTimestamp(params?.row.timestamp)}
            text={formatTimestamp(params?.row.timestamp)}
            TypographyProps={{
              variant: "body2",
            }}
          />
        ) : null,
    },
    {
      field: "video",
      headerName: "Video",
      flex: 1,
      minWidth: 400,
      width: 400,
      hideable: false,
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params): JSX.Element | null => {
        const nodeId = params?.row?.nodeId;
        const deviceId = params?.row?.cameraId;
        // const timestamp = params?.row.timestamp; // this is the local  timestamp converted from UTC
        const originalTimeInUTC = params?.row.originalTimeInUTC;
        const s3Key = params?.row.mediaOutput;

        const { exists, loading, checkExists } = useS3ObjectExists(
          S3BucketKeys.NODE_FILES as S3BucketKeyType,
          s3Key,
          params?.row.videoStatus
        );

        const [notRetrieved, setNotRetrieved] = useState(
          params?.row.videoStatus === VideoStatusEnum.NOT_RETRIEVED && !exists
        );

        const [isRetrieveVideoProcessing, setIsRetrieveVideoProcessing] =
          useState(params?.row?.isRetrieving ?? false);

        const handleRetrieveVideo = () => {
          setIsRetrieveVideoProcessing(true);

          setTimeout(() => {
            checkExists().then(() => {
              if (exists) {
                setNotRetrieved(false);

                setIsRetrieveVideoProcessing(false);
              }
            });
          }, 7000);
        };

        return (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              padding: "0.5em 0",
              gap: "0.5em",
            }}
          >
            <LiveViewButtonContainer
              cameraId={params.row.cameraId}
              nodeId={params.row.nodeId ?? ""}
            />
            {notRetrieved ? (
              <RetrieveVideoButton
                isRetrieveVideoProcessing={isRetrieveVideoProcessing}
                handleRetrieveVideo={handleRetrieveVideo}
                nodeId={nodeId}
                deviceId={deviceId}
                timestamp={originalTimeInUTC ?? ""}
                s3Key={s3Key}
                row={params.row}
              />
            ) : (
              <S3MediaActions s3Key={s3Key} loading={loading} exists={exists} />
            )}
          </Box>
        );
      },
    },
  ];

  return { columns };
};
